import { createSlice, PayloadAction } from '@reduxjs/toolkit'

// ============================================
// CARD STATE TYPES
// ============================================

// Candidate Profile Card
export interface ApplicantProfileState {
    image: ApplicantPhoto | null
    name: { firstName: string; lastName: string }
    location: string | null
    phone: ApplicantPhone[] | null
    email: ApplicantEmail[] | null
    teams: ApplicantSocialLink | null
    skype: ApplicantSocialLink | null
    nickname: string | null
    last_login: LastLogin | null
    hired_at: string | null
    user_deleted_at: string | null
    gender: string | null
    applied_on_diff: string | null
    timezone: string | null
    primaryEmail: string | null
    isUnsubscribed: boolean | null
    hasRecommendedTag: boolean | null
    rating: {
        [key: string]: any
    } | null
    /** ⚠️ Holds the candidate UUID (`data.id`), not `users.id` — see `applicant_id`. */
    user_id?: number | null
    /** Integer `users.id` from the candidate base response; what the position recommend endpoint wants. */
    applicant_id?: number | null

}


// Recordings Card
export interface RecordingsState {
    interviewRecordings: ApplicantRecording[]
    zoomRecordings: ApplicantZoomRecording[]
}

// Email Logs Card
export interface EmailLogsState {
    emailLogs: ApplicantEmailLog[]
}

// Activity Logs Card
export interface ActivityLog {
    id: string
    action: string
    timestamp: string
    [key: string]: any
}

export interface ActivityLogsState {
    activityLogs: ActivityLog[]
}

// Basic Information Card
export interface BasicInformationState {
    languages: string | null
    location: string | null
    referredBy: ApplicantApplicationReferrer[] | null
    shortBio: string | null
    socialProfiles: {
        "facebook": ApplicantSocialLink | null
        "linkedin": ApplicantSocialLink | null
        "twitter": ApplicantSocialLink | null
        "skype": ApplicantSocialLink | null
        "discord": ApplicantSocialLink | null
        "telegram": ApplicantSocialLink | null
        "slack": ApplicantSocialLink | null
    }
}

// Address Information Card
export interface AddressInformationState {
    addresses: ApplicantAddress[]
}


// Portfolio Card
export interface PortfolioItem {
    id: string
    url: string
    title?: string
    description?: string
}

export interface PortfolioState {
    portfolioLinks: PortfolioItem[]
}

// Interview Questions Card
export interface InterviewQuestionsState {
    questions: ApplicantInterviewQuestion[]
}




// ============================================
// MAIN STATE
// ============================================

export interface ApplicantDetailsState {
    // Meta
    applicantId: string | null
    isLoading: boolean
    appliedJobs: ApplicantAppliedJob[]
    // Card States
    applicantProfile: ApplicantProfileState
    recruiterNotes: ApplicantRecruiterNote[]
    applicantTags: ProfileTag[]
    recordings: RecordingsState
    emailLogs: EmailLogsState
    activityLogs: ActivityLogsState
    basicInformation: BasicInformationState
    addressInformation: AddressInformationState
    indeedAssessment: ApplicantIndeedAssessmentScore[]
    resumes: ApplicantResume[]
    currentResume: ApplicantResume | null
    showParsedResumeModal: boolean
    writtenAssignments: ApplicantWrittenAssignment[]
    portfolio: PortfolioState
    interviewQuestions: ApplicantInterviewQuestion[]
    screeningQuestions: ApplicantJobQuestions[]
    selectedJob: ApplicantAppliedJob | null
    dataBeforeHired: DataBeforeHired | null
    visibility?: number[]
    employeeInformation?: ApplicantPersonalInfoResponse | null
    isEmployee: boolean
}

// ============================================
// INITIAL STATE
// ============================================

const initialState: ApplicantDetailsState = {
    applicantId: null,
    isLoading: false,

    dataBeforeHired: null,

    applicantProfile: {
        image: null,
        name: { firstName: '', lastName: '' },
        location: null,
        phone: null,
        email: null,
        teams: null,
        skype: null,
        nickname: null,
        hired_at: null,
        last_login: null,
        user_deleted_at: null,
        gender: null,
        applied_on_diff: null,
        timezone: null,
        primaryEmail: null,
        isUnsubscribed: null,
        hasRecommendedTag: false,
        rating: null,
        applicant_id: null,
    },

    recruiterNotes: [],
    applicantTags: [],
    appliedJobs: [],
    recordings: {
        interviewRecordings: [],
        zoomRecordings: [],
    },

    emailLogs: {
        emailLogs: [],
    },

    activityLogs: {
        activityLogs: [],
    },

    basicInformation: {
        location: null,
        languages: null,
        referredBy: null,
        shortBio: null,
        socialProfiles: {
            linkedin: null,
            twitter: null,
            facebook: null,
            skype: null,
            telegram: null,
            discord: null,
            slack: null,

        },
    },

    addressInformation: {
        addresses: [],
    },


    indeedAssessment: [],



    resumes: [],
    currentResume: null,
    showParsedResumeModal: false,

    writtenAssignments: [],


    portfolio: {
        portfolioLinks: [],
    },
    interviewQuestions: [],

    screeningQuestions: [],

    selectedJob: null,
    visibility: [],

    employeeInformation: null,
    isEmployee: false,
}

// ============================================
// SLICE
// ============================================

const applicantDetailsSlice = createSlice({
    name: 'applicantDetails',
    initialState,
    reducers: {
        // Meta
        setApplicantId: (state, action: PayloadAction<string | null>) => {
            state.applicantId = action.payload
        },
        setIsLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload
        },
        resetApplicantDetails: () => initialState,

        setAppliedJobs: (state, action: PayloadAction<ApplicantAppliedJob[]>) => {
            state.appliedJobs = action.payload
        },

        setSelectedJob: (state, action: PayloadAction<ApplicantAppliedJob | null>) => {
            state.selectedJob = action.payload
        },

        setRating: (state, action: PayloadAction<{ [key: string]: any } | null>) => {
            state.applicantProfile.rating = action.payload
        },
        setVisibility: (state, action: PayloadAction<number[]>) => {
            state.visibility = action.payload
        },
        addVisibility: (state, action: PayloadAction<number>) => {
            if (!state.visibility) {
                state.visibility = []
            }
            if (!state.visibility.includes(action.payload)) {
                state.visibility.push(action.payload)
            }
        },
        removeVisibility: (state, action: PayloadAction<number>) => {
            if (state.visibility) {
                state.visibility = state.visibility.filter(id => id !== action.payload)
            }
        },
        // ==========================================
        // CANDIDATE PROFILE CARD
        // ==========================================
        setApplicantProfile: (state, action: PayloadAction<Partial<ApplicantProfileState>>) => {
            state.applicantProfile = { ...state.applicantProfile, ...action.payload }
        },
        setProfileImage: (state, action: PayloadAction<ApplicantPhoto | null>) => {
            state.applicantProfile.image = action.payload
        },
        setProfileName: (state, action: PayloadAction<{ firstName?: string; lastName?: string }>) => {
            if (action.payload.firstName !== undefined) {
                state.applicantProfile.name.firstName = action.payload.firstName
            }
            if (action.payload.lastName !== undefined) {
                state.applicantProfile.name.lastName = action.payload.lastName
            }
        },
        setProfileLocation: (state, action: PayloadAction<string | null>) => {
            state.applicantProfile.location = action.payload
        },
        setProfilePhone: (state, action: PayloadAction<ApplicantPhone[] | null>) => {
            state.applicantProfile.phone = action.payload
        },
        setProfileEmail: (state, action: PayloadAction<ApplicantEmail[] | null>) => {
            state.applicantProfile.email = action.payload
        },
        setProfileTeams: (state, action: PayloadAction<ApplicantSocialLink | null>) => {
            state.applicantProfile.teams = action.payload
        },
        setProfileSkype: (state, action: PayloadAction<ApplicantSocialLink | null>) => {
            state.applicantProfile.skype = action.payload
        },
        setProfileNickname: (state, action: PayloadAction<string | null>) => {
            state.applicantProfile.nickname = action.payload
        },
        setProfileLastActive: (state, action: PayloadAction<LastLogin | null>) => {
            state.applicantProfile.last_login = action.payload
        },


        // ==========================================
        // TAGS CARD
        // ==========================================
        setApplicantTags: (state, action: PayloadAction<ProfileTag[]>) => {
            state.applicantTags = action.payload
        },

        // ==========================================
        // RECRUITER NOTES CARD
        // ==========================================
        setRecruiterNotes: (state, action: PayloadAction<ApplicantRecruiterNote[]>) => {
            state.recruiterNotes = action.payload
        },
        addRecruiterNote: (state, action: PayloadAction<ApplicantRecruiterNote>) => {
            state.recruiterNotes = [action.payload, ...state.recruiterNotes]
        },
        updateRecruiterNote: (state, action: PayloadAction<ApplicantRecruiterNote>) => {
            const index = state.recruiterNotes.findIndex(n => n.id === action.payload.id)
            if (index !== -1) {
                state.recruiterNotes[index] = action.payload
            }
        },
        deleteRecruiterNote: (state, action: PayloadAction<number>) => {
            state.recruiterNotes = state.recruiterNotes.filter(n => n.id !== action.payload)
        },

        // ==========================================
        // RECORDINGS CARD
        // ==========================================
        setRecordings: (state, action: PayloadAction<ApplicantRecording[]>) => {
            state.recordings.interviewRecordings = action.payload
        },
        setZoomRecordings: (state, action: PayloadAction<ApplicantZoomRecording[]>) => {
            state.recordings.zoomRecordings = action.payload
        },
        addRecording: (state, action: PayloadAction<ApplicantRecording>) => {
            state.recordings.interviewRecordings.push(action.payload)
        },
        deleteRecording: (state, action: PayloadAction<number>) => {
            state.recordings.interviewRecordings = state.recordings.interviewRecordings.filter(
                r => r.id !== action.payload
            )
        },

        // ==========================================
        // EMAIL LOGS CARD
        // ==========================================
        setEmailLogs: (state, action: PayloadAction<ApplicantEmailLog[]>) => {
            state.emailLogs.emailLogs = action.payload
        },

        // ==========================================
        // ACTIVITY LOGS CARD
        // ==========================================
        setActivityLogs: (state, action: PayloadAction<ActivityLog[]>) => {
            state.activityLogs.activityLogs = action.payload
        },

        // ==========================================
        // BASIC INFORMATION CARD
        // ==========================================
        setBasicInformation: (state, action: PayloadAction<Partial<BasicInformationState>>) => {
            state.basicInformation = { ...state.basicInformation, ...action.payload }
        },
        setBasicInfoName: (state, action: PayloadAction<{ firstName?: string; lastName?: string }>) => {
            if (action.payload.firstName !== undefined) {
                state.applicantProfile.name.firstName = action.payload.firstName
            }
            if (action.payload.lastName !== undefined) {
                state.applicantProfile.name.lastName = action.payload.lastName
            }
        },
        setBasicInfoBirthday: (state, action: PayloadAction<string | null>) => {
            state.basicInformation.birthday = action.payload
        },
        setBasicInfoNationality: (state, action: PayloadAction<string | null>) => {
            state.basicInformation.nationality = action.payload
        },
        setBasicInfoLanguages: (state, action: PayloadAction<string | null>) => {
            state.basicInformation.languages = action.payload
        },
        setBasicInfoReferredBy: (state, action: PayloadAction<ApplicantApplicationReferrer[] | null>) => {
            state.basicInformation.referredBy = action.payload
        },
        setBasicInfoShortBio: (state, action: PayloadAction<string | null>) => {
            state.basicInformation.shortBio = action.payload
        },
        setBasicInfoSocialProfiles: (state, action: PayloadAction<Partial<BasicInformationState['socialProfiles']>>) => {
            state.basicInformation.socialProfiles = {
                ...state.basicInformation.socialProfiles,
                ...action.payload,
            }
        },

        // ==========================================
        // ADDRESS INFORMATION CARD
        // ==========================================
        setAddressInformation: (state, action: PayloadAction<ApplicantAddress[]>) => {
            state.addressInformation.addresses = action.payload
        },
        addAddress: (state, action: PayloadAction<ApplicantAddress>) => {
            state.addressInformation.addresses.push(action.payload)
        },
        updateAddress: (state, action: PayloadAction<ApplicantAddress>) => {
            const index = state.addressInformation.addresses.findIndex(a => a.id === action.payload.id)
            if (index !== -1) {
                state.addressInformation.addresses[index] = action.payload
            }
        },
        deleteAddress: (state, action: PayloadAction<number>) => {
            state.addressInformation.addresses = state.addressInformation.addresses.filter(
                a => a.id !== action.payload
            )
        },

        // ==========================================
        // ASSESSMENT CARD
        // ==========================================
        setIndeedAssessmentResults: (state, action: PayloadAction<ApplicantIndeedAssessmentScore[]>) => {
            state.indeedAssessment = action.payload
        },
        addIndeedAssessmentResult: (state, action: PayloadAction<ApplicantIndeedAssessmentScore>) => {
            state.indeedAssessment.push(action.payload)
        },

        // ==========================================
        // RESUME CARD
        // ==========================================
        setResumes: (state, action: PayloadAction<ApplicantResume[]>) => {
            state.resumes = action.payload
        },
        setCurrentResume: (state, action: PayloadAction<ApplicantResume | null>) => {
            state.currentResume = action.payload
        },
        addResume: (state, action: PayloadAction<ApplicantResume>) => {
            state.resumes.push(action.payload)
        },
        deleteResume: (state, action: PayloadAction<string>) => {
            state.resumes = state.resumes.filter(r => r.id !== action.payload)
        },
        setShowParsedResumeModal: (state, action: PayloadAction<boolean>) => {
            state.showParsedResumeModal = action.payload
        },

        // ==========================================
        // WRITTEN ASSIGNMENTS CARD
        // ==========================================
        setWrittenAssignments: (state, action: PayloadAction<ApplicantWrittenAssignment[]>) => {
            state.writtenAssignments = action.payload
        },
        updateWrittenAssignment: (state, action: PayloadAction<ApplicantWrittenAssignment>) => {
            const index = state.writtenAssignments.findIndex(
                a => a.question_id === action.payload.question_id
            )
            if (index !== -1) {
                state.writtenAssignments[index] = action.payload
            }
        },

        // ==========================================
        // PORTFOLIO CARD
        // ==========================================
        setPortfolioLinks: (state, action: PayloadAction<PortfolioItem[]>) => {
            state.portfolio.portfolioLinks = action.payload
        },
        addPortfolioLink: (state, action: PayloadAction<PortfolioItem>) => {
            state.portfolio.portfolioLinks.push(action.payload)
        },
        deletePortfolioLink: (state, action: PayloadAction<string>) => {
            state.portfolio.portfolioLinks = state.portfolio.portfolioLinks.filter(
                p => p.id !== action.payload
            )
        },

        // ==========================================
        // INTERVIEW QUESTIONS CARD
        // ==========================================
        setInterviewQuestions: (state, action: PayloadAction<ApplicantInterviewQuestion[]>) => {
            state.interviewQuestions = action.payload
        },
        updateInterviewQuestion: (state, action: PayloadAction<ApplicantInterviewQuestion>) => {
            const index = state.interviewQuestions.findIndex(
                q => q.field_id === action.payload.field_id
            )
            if (index !== -1) {
                state.interviewQuestions[index] = action.payload
            }
        },

        // ==========================================
        // SCREENING QUESTIONS CARD
        // ==========================================
        setScreeningQuestions: (state, action: PayloadAction<ApplicantJobQuestions[]>) => {
            state.screeningQuestions = action.payload
        },
        updateScreeningQuestion: (state, action: PayloadAction<ApplicantJobQuestions>) => {
            const index = state.screeningQuestions.findIndex(
                q => q.question_id === action.payload.question_id
            )
            if (index !== -1) {
                state.screeningQuestions[index] = action.payload
            }
        },

        setDataBeforeHired: (state, action: PayloadAction<DataBeforeHired | null>) => {
            state.dataBeforeHired = action.payload
        },

        // ==========================================
        // EMPLOYEE INFORMATION
        // ==========================================
        setEmployeeInformation: (state, action: PayloadAction<ApplicantPersonalInfoResponse | null>) => {
            state.employeeInformation = action.payload
        },
        setIsEmployee: (state, action: PayloadAction<boolean>) => {
            state.isEmployee = action.payload
        }
    },
})

// Export actions
export const {
    // Meta
    setApplicantId,
    setIsLoading,
    resetApplicantDetails,
    setAppliedJobs,
    setSelectedJob,
    // Candidate Profile
    setApplicantProfile,
    setProfileImage,
    setProfileName,
    setProfileLocation,
    setProfilePhone,
    setProfileEmail,
    setProfileTeams,
    setProfileSkype,
    setProfileNickname,
    setProfileLastActive,
    setRating,

    // Tags
    setApplicantTags,

    // Recruiter Notes
    setRecruiterNotes,
    addRecruiterNote,
    updateRecruiterNote,
    deleteRecruiterNote,

    // Recordings
    setRecordings,
    setZoomRecordings,
    addRecording,
    deleteRecording,

    // Email Logs
    setEmailLogs,

    // Activity Logs
    setActivityLogs,

    // Basic Information
    setBasicInformation,
    setBasicInfoName,
    setBasicInfoBirthday,
    setBasicInfoNationality,
    setBasicInfoLanguages,
    setBasicInfoReferredBy,
    setBasicInfoShortBio,
    setBasicInfoSocialProfiles,

    // Address Information
    setAddressInformation,
    addAddress,
    updateAddress,
    deleteAddress,

    // Assessment
    setIndeedAssessmentResults,
    addIndeedAssessmentResult,

    // Resume
    setResumes,
    setCurrentResume,
    addResume,
    deleteResume,
    setShowParsedResumeModal,

    // Written Assignments
    setWrittenAssignments,
    updateWrittenAssignment,

    // Portfolio
    setPortfolioLinks,
    addPortfolioLink,
    deletePortfolioLink,

    // Interview Questions
    setInterviewQuestions,
    updateInterviewQuestion,

    // Screening Questions
    setScreeningQuestions,
    updateScreeningQuestion,
    setDataBeforeHired,


    addVisibility,
    removeVisibility,
    setVisibility,

    setIsEmployee,
    setEmployeeInformation,
} = applicantDetailsSlice.actions

// Export reducer
export default applicantDetailsSlice.reducer
